import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';

// The timeline logger's focus cursor: which exercise is active, which set is
// selected, and whether the roster is expanded. Held HERE — called from the
// LoggerScreen body, ABOVE the <Modal> — rather than inside useTimelineLogger,
// which runs inside the Modal. Minimize toggles the Modal's visible=false and
// RN unmounts its whole subtree, which used to destroy the cursor useState so
// resume reopened without your place (LG-13). The draft already lives above the
// Modal (useLoggerScreen) for the same reason (LG-10c); the cursor now shares
// that durable home and survives minimize -> resume.
//
// LoggerScreen is a persistent singleton (the host never remounts, only the
// Modal's visibility toggles), so unlike the old in-Modal useState this state
// no longer gets a free reset between workouts. We reset it explicitly when a
// new session starts, keyed on draft.startedAt — the same signal the rest timer
// (useLoggerScreen) and useBodyweightPrompt use. A new session re-expands the
// roster (the self-teaching first screen) and drops any stale set selection;
// activeId is already guarded by resolvedActiveId but is reset here for
// cleanliness. Minimize -> resume keeps the SAME startedAt, so it does NOT
// reset — that is exactly the survival LG-13 needs. (Cold boot resets it too,
// which is correct: Option B, no auto-focus on rehydrate, you re-pick.)
export type LoggerCursor = {
  activeId: string | null;
  setActiveId: Dispatch<SetStateAction<string | null>>;
  selectedSetIdx: number | null;
  setSelectedSetIdx: Dispatch<SetStateAction<number | null>>;
  rosterExpanded: boolean;
  setRosterExpanded: Dispatch<SetStateAction<boolean>>;
};

export function useLoggerCursor(sessionKey: string): LoggerCursor {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedSetIdx, setSelectedSetIdx] = useState<number | null>(null);
  const [rosterExpanded, setRosterExpanded] = useState(true);

  useEffect(() => {
    setActiveId(null);
    setSelectedSetIdx(null);
    setRosterExpanded(true);
  }, [sessionKey]);

  return {
    activeId,
    setActiveId,
    selectedSetIdx,
    setSelectedSetIdx,
    rosterExpanded,
    setRosterExpanded,
  };
}
